import type { ReactNode } from 'react';
import { office_module } from './office/module';
import { linkedin_module } from './linkedin/module';
import { business360_module } from './business360/module';

// The app-module contract (docs/console.md — P8). Each app the console hosts contributes one
// module: a manifest (nav identity + branding) + bespoke feature views. The shell renders nav
// only for the modules whose app_code the logged-in tenant has provisioned.
export interface NavEntry {
  path: string; // relative to /a/:app_code
  label: string;
  element: ReactNode;
}
export interface AppModule {
  app_code: string;
  label: string;
  nav: NavEntry[];
  // Branding for the sidebar + the overview launcher (optional; the shell has fallbacks).
  blurb?: string;
  icon?: ReactNode;
  accent?: string; // CSS color for the app's icon tile
}

const REGISTRY: Record<string, AppModule> = {
  [office_module.app_code]: office_module,
  [linkedin_module.app_code]: linkedin_module,
  [business360_module.app_code]: business360_module,
};

// Modules for the apps a tenant has, in the tenant's app order, skipping any app_code the
// console has no module for yet (forward-compatible: a newly-provisioned app simply doesn't
// render until its module ships — C6 / D5 graceful-unknown).
export function modules_for(apps: string[]): AppModule[] {
  return apps.map((a) => REGISTRY[a]).filter((m): m is AppModule => Boolean(m));
}

export function module_for(app_code: string): AppModule | undefined {
  return REGISTRY[app_code];
}

// The first navigable route for a module (where an app card / sidebar app label links to).
export function first_route(m: AppModule): string {
  return m.nav.length ? `/a/${m.app_code}/${m.nav[0].path}` : '/';
}
